/**
 * Workout repository – Firestore CRUD + list/stream. Observer for realtime feed.
 */
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  setDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  startAfter,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { createWorkout } from '../../domain/entities/Workout';

const COLLECTION = 'workouts';

export function workoutRepository({ getFirestore = () => db } = {}) {
  const firestore = getFirestore();
  const coll = () => collection(firestore, COLLECTION);

  return {
    async getById(workoutId) {
      const ref = doc(firestore, COLLECTION, workoutId);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return createWorkout({ id: snap.id, ...snap.data() });
    },

    async create(data) {
      const ref = await addDoc(coll(), {
        ...data,
        showInFeed: data.showInFeed === true,
        createdAt: new Date(),
      });
      return ref.id;
    },

    async update(workoutId, data) {
      const ref = doc(firestore, COLLECTION, workoutId);
      await setDoc(ref, { ...data, updatedAt: new Date() }, { merge: true });
    },

    async delete(workoutId) {
      const ref = doc(firestore, COLLECTION, workoutId);
      await deleteDoc(ref);
    },

    async listFeed(limitCount = 20, afterDoc = null) {
      let q = query(
        coll(),
        where('showInFeed', '==', true),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );
      if (afterDoc) q = query(q, startAfter(afterDoc));
      const snap = await getDocs(q);
      const workouts = [];
      snap.forEach((d) => workouts.push(createWorkout({ id: d.id, ...d.data() })));
      return { items: workouts, lastDoc: snap.docs[snap.docs.length - 1] ?? null };
    },

    subscribeFeed(limitCount, onUpdate) {
      const q = query(
        coll(),
        where('showInFeed', '==', true),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );
      return onSnapshot(q, (snap) => {
        const items = [];
        snap.forEach((d) => items.push(createWorkout({ id: d.id, ...d.data() })));
        onUpdate(items);
      });
    },

    /**
     * Realtime feed with optional filters: exercise (exact name) and/or discipline.
     * Only workouts with showInFeed === true (one per user). Requires composite indexes.
     */
    subscribeFeedFiltered(limitCount, filters, onUpdate) {
      const { exercise = null, discipline = null } = filters || {};
      let q = query(coll(), where('showInFeed', '==', true));
      if (exercise && exercise.trim() !== '') q = query(q, where('exercise', '==', exercise.trim()));
      if (discipline && discipline.trim() !== '') q = query(q, where('discipline', '==', discipline.trim()));
      q = query(q, orderBy('createdAt', 'desc'), limit(limitCount));
      return onSnapshot(q, (snap) => {
        const items = [];
        snap.forEach((d) => items.push(createWorkout({ id: d.id, ...d.data() })));
        onUpdate(items);
      });
    },

    /** Set the single workout that appears in the feed for this user. Clears showInFeed on others. */
    async setUserFeedWorkout(userId, workoutId) {
      const others = await this.listByUser(userId, 500);
      await Promise.all(
        others.map((w) =>
          this.update(w.id, { showInFeed: w.id === workoutId })
        )
      );
    },

    async listByUser(userId, limitCount = 50) {
      const q = query(
        coll(),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );
      const snap = await getDocs(q);
      const items = [];
      snap.forEach((d) => items.push(createWorkout({ id: d.id, ...d.data() })));
      return items;
    },

    subscribeByUser(userId, limitCount, onUpdate) {
      const q = query(
        coll(),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );
      return onSnapshot(q, (snap) => {
        const items = [];
        snap.forEach((d) => items.push(createWorkout({ id: d.id, ...d.data() })));
        onUpdate(items);
      });
    },
  };
}

export const workoutRepo = workoutRepository();
